import React, { useMemo, useState, useEffect } from 'react';
import { Table, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Api from '../../Network';
import NameByLang from '../nameByLang';

interface PermissionsTableProps {
  selectedPermissions?: number[];
  onChange?: (permissionIds: number[]) => void;
  initialPermissions?: any[];
  filterByModels?: any[]; // Optional: Filter models by role's assigned models
}

/** Laravel list payloads sometimes nest as data.data or data.data.data */
type AdminNestedListBody = { data?: { data?: unknown[] | { data?: unknown[] } } };

function unwrapAdminNestedList(body: AdminNestedListBody | undefined): any[] {
  const inner = body?.data?.data;
  if (!inner) return [];
  if (Array.isArray(inner)) return inner;
  const nested = (inner as { data?: unknown[] }).data;
  return Array.isArray(nested) ? nested : [];
}

const PermissionsTable: React.FC<PermissionsTableProps> = ({
  selectedPermissions,
  onChange,
  initialPermissions = [],
  filterByModels = [],
}) => {
  const { t } = useTranslation();
  const [internalSelected, setInternalSelected] = useState<number[]>([]);

  // Fetch all models with their permissions
  const { data: modelsData } = useQuery({
    queryKey: ['/api/admin/models'],
    queryFn: () => Api.get<AdminNestedListBody>('/api/admin/models'),
  });

  // Fetch all permissions
  const { data: allPermissionsData } = useQuery({
    queryKey: ['/api/admin/permissions'],
    queryFn: () => Api.get<AdminNestedListBody>('/api/admin/permissions'),
  });

  const allModels = unwrapAdminNestedList(modelsData);

  const models = useMemo(() => {
    // Only show menu models explicitly assigned to the role/user (Models tab).
    // Do NOT inject rows because the role has permissions linked via model_permission elsewhere.
    if (!filterByModels || filterByModels.length === 0) {
      return [];
    }

    const filterModelIds = new Set(filterByModels.map((m: any) => Number(m.id ?? m)));
    return allModels.filter((model: any) => filterModelIds.has(Number(model.id)));
  }, [allModels, filterByModels]);

  const allPermissions = unwrapAdminNestedList(allPermissionsData);

  // Initialize selected permissions
  useEffect(() => {
    // When selectedPermissions is provided (even if empty), use it (controlled component)
    // Only fall back to initialPermissions when selectedPermissions is undefined
    if (selectedPermissions !== undefined) {
      setInternalSelected(
        selectedPermissions.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      );
    } else if (initialPermissions && initialPermissions.length > 0) {
      const ids = initialPermissions
        .map((p: any) => Number(p.id ?? p))
        .filter((id) => !Number.isNaN(id));
      setInternalSelected(ids);
    } else {
      setInternalSelected([]);
    }
  }, [initialPermissions, selectedPermissions]);

  // Collect all permissions that are already categorized by models (filtered models only)
  const categorizedPermissionIds = useMemo(() => {
    const ids = new Set<number>();

    models.forEach((model: any) => {
      const modelPermissions = model.permissions || [];
      modelPermissions.forEach((perm: any) => {
        if (perm.id) {
          ids.add(perm.id);
        }
      });
    });

    return ids;
  }, [models]);

  // Group permissions by model and action type
  const permissionsByModel = useMemo(() => {
    const grouped: Record<string, any> = {};

    models.forEach((model: any) => {
      const modelId = model.id;
      // Only permissions linked to this menu model (model_permission) — never inject all role perms per row
      const modelPermissions = model.permissions || [];

      // Categorize permissions by action type
      const categorized: Record<string, any> = {};

      const otherPermissions: any[] = [];

      const modelKey = model.key || '';
      const isCustomersModel = modelKey === '/customers';
      const isVisitsModel = modelKey === '/visits';

      modelPermissions.forEach((perm: any) => {
        const permName = perm.name || '';
        const permCategory = perm.category; // Check if permission has explicit category
        let actionType = '';
        let isCategorized = false;

        // PRIORITY 1: If permission has explicit category set, use it
        // "reports" is a logical grouping only — map via name patterns (view / download columns)
        const tableColumnCategories = new Set([
          'view_all',
          'view_details',
          'create',
          'update',
          'delete',
        ]);
        if (permCategory && permCategory !== 'reports') {
          if (permCategory === 'other') {
            otherPermissions.push(perm);
            return;
          }
          if (tableColumnCategories.has(permCategory)) {
            actionType = permCategory;
            isCategorized = true;
          }
        }

        // PRIORITY 2: Special handling for Customers model
        if (!isCategorized && isCustomersModel) {
          if (permName === 'view_customers') {
            actionType = 'view_all';
            isCategorized = true;
          } else if (permName === 'view_customer') {
            actionType = 'view_details';
            isCategorized = true;
          } else if (
            permName === 'update_customer' ||
            permName === 'block_customer' ||
            permName === 'customers.block'
          ) {
            actionType = 'update';
            isCategorized = true;
          } else {
            // All other permissions (including customer_classifications) go to "other" column
            otherPermissions.push(perm);
            return;
          }
        }

        // PRIORITY 3: Model-specific patterns
        if (!isCategorized) {
          if (modelKey === '/reports/sales') {
            if (permName === 'sales_report.view') {
              actionType = 'view_all';
              isCategorized = true;
            } else if (
              permName === 'export_sales_report' ||
              permName === 'export_reports' ||
              permName === 'sales_report.download'
            ) {
              otherPermissions.push(perm);
              return;
            }
          } else if (modelKey === '/reports/sales-analytics') {
            if (permName === 'view_sales_analytics' || permName === 'sales_analytics.view') {
              actionType = 'view_all';
              isCategorized = true;
            } else if (
              permName === 'download_sales_analytics' ||
              permName === 'sales_analytics.download'
            ) {
              otherPermissions.push(perm);
              return;
            }
          } else if (isVisitsModel) {
            // Show view_all, view_details, update columns
            // Download report goes to "other" column
            if (permName === 'view_visits') {
              actionType = 'view_all';
              isCategorized = true;
            } else if (permName === 'view_visit') {
              actionType = 'view_details';
              isCategorized = true;
            } else if (permName === 'update_visit') {
              actionType = 'update';
              isCategorized = true;
            } else if (permName.includes('download') || permName.includes('report')) {
              otherPermissions.push(perm);
              return;
            }
          }
        }

        // PRIORITY 4: Generic name patterns (only if not already categorized — avoids re‑routing export_sales_report into "other")
        if (!isCategorized) {
          if (permName.startsWith('view_')) {
            // Check if it's view_all (plural like view_orders) or view_details (singular like view_order)
            const resource = permName.replace('view_', '');

            // Special cases
            if (resource === 'dashboard') {
              actionType = 'view_all'; // Dashboard is always view_all
              isCategorized = true;
            } else {
              // Plural forms typically end with 's' (but not 'ss' or words ending in 's')
              // Examples: orders (plural), order (singular), customers (plural), customer (singular)
              const isPlural =
                resource.endsWith('s') &&
                !resource.endsWith('ss') &&
                resource !== 'settings' &&
                !['users', 'roles', 'permissions', 'models'].includes(resource);
              actionType = isPlural ? 'view_all' : 'view_details';
              isCategorized = true;
            }
          } else if (permName.startsWith('create_')) {
            actionType = 'create';
            isCategorized = true;
          } else if (permName.startsWith('update_')) {
            actionType = 'update';
            isCategorized = true;
          } else if (permName.startsWith('delete_')) {
            actionType = 'delete';
            isCategorized = true;
          } else if (permName.includes('.read') || permName.includes('.view')) {
            // Pattern like: payment_methods.read, customer_types.read
            actionType = 'view_all';
            isCategorized = true;
          } else if (permName.includes('.create')) {
            // Pattern like: payment_methods.create, customer_types.create
            actionType = 'create';
            isCategorized = true;
          } else if (permName.includes('.update')) {
            // Pattern like: payment_methods.update, customer_types.update
            actionType = 'update';
            isCategorized = true;
          } else if (permName.includes('.delete')) {
            // Pattern like: payment_methods.delete, customer_types.delete
            actionType = 'delete';
            isCategorized = true;
          } else if (
            permName.includes('download') ||
            permName.includes('report') ||
            permName.includes('export')
          ) {
            // Download report permissions go to "other" column
            otherPermissions.push(perm);
            return;
          }
        }

        // Only add if action type is recognized
        // For view permissions, prefer view_details over view_all if both exist
        if (isCategorized && actionType) {
          if (actionType === 'view_all' && categorized['view_all']) {
            // If we already have view_all, add this to other if it's different
            if (categorized['view_all'].id !== perm.id) {
              otherPermissions.push(perm);
            }
            return;
          }
          if (actionType === 'view_details' && categorized['view_details']) {
            // If we already have view_details, add this to other if it's different
            if (categorized['view_details'].id !== perm.id) {
              otherPermissions.push(perm);
            }
            return;
          }
          categorized[actionType] = perm;
        } else {
          // Add uncategorized permissions to "other"
          otherPermissions.push(perm);
        }
      });

      // Add other permissions if any
      if (otherPermissions.length > 0) {
        categorized.other = otherPermissions;
      }

      grouped[modelId] = {
        model,
        permissions: categorized,
      };
    });

    return grouped;
  }, [models]);

  // Get uncategorized permissions (not linked to any model)
  const uncategorizedPermissions = useMemo(() => {
    return allPermissions.filter((perm: any) => {
      // Skip if already categorized in a model
      if (categorizedPermissionIds.has(perm.id)) {
        return false;
      }
      // Include all other permissions
      return true;
    });
  }, [allPermissions, categorizedPermissionIds]);

  // Convert grouped data to table rows
  const tableData = useMemo(() => {
    const rows = Object.values(permissionsByModel).map((item: any) => ({
      key: item.model.id,
      model: item.model,
      permissions: item.permissions,
      isUncategorized: false,
    }));

    if (uncategorizedPermissions.length > 0 && (!filterByModels || filterByModels.length === 0)) {
      rows.push({
        key: 'uncategorized',
        model: {
          id: 'uncategorized',
          name: t('permissions.uncategorized', 'Other Permissions'),
          name_en: 'Other Permissions',
          name_ar: 'صلاحيات أخرى',
          key: 'other',
        },
        permissions: {
          other: uncategorizedPermissions,
        },
        isUncategorized: true,
      });
    }

    return rows;
  }, [permissionsByModel, uncategorizedPermissions, filterByModels, t]);

  // Handle checkbox change
  const handlePermissionChange = (permissionId: number, checked: boolean) => {
    const newSelected = checked
      ? [...internalSelected, permissionId]
      : internalSelected.filter((id: number) => id !== permissionId);

    setInternalSelected(newSelected);
    onChange?.(newSelected);
  };

  // Get all permission IDs for a specific column across all rows
  const getColumnPermissionIds = (actionType: string): number[] => {
    const permIds: number[] = [];
    tableData.forEach((row: any) => {
      if (actionType === 'other') {
        const otherPerms = row.permissions.other;
        if (otherPerms && Array.isArray(otherPerms)) {
          otherPerms.forEach((perm: any) => {
            if (perm.id) permIds.push(perm.id);
          });
        }
      } else {
        const perm = row.permissions[actionType];
        if (perm && perm.id) {
          permIds.push(perm.id);
        }
      }
    });
    return permIds;
  };

  // Handle select all for a column
  const handleSelectAllColumn = (actionType: string, checked: boolean) => {
    const columnPermIds = getColumnPermissionIds(actionType);
    let newSelected: number[];

    if (checked) {
      // Add all column permissions to selection (remove duplicates)
      const combined = [...internalSelected, ...columnPermIds];
      newSelected = Array.from(new Set(combined));
    } else {
      // Remove all column permissions from selection
      newSelected = internalSelected.filter((id) => !columnPermIds.includes(id));
    }

    setInternalSelected(newSelected);
    onChange?.(newSelected);
  };

  // Check if all permissions in a column are selected
  const isColumnAllSelected = (actionType: string): boolean => {
    const columnPermIds = getColumnPermissionIds(actionType);
    if (columnPermIds.length === 0) return false;
    return columnPermIds.every((id) => internalSelected.includes(id));
  };

  // Check if some but not all permissions in a column are selected
  const isColumnIndeterminate = (actionType: string): boolean => {
    const columnPermIds = getColumnPermissionIds(actionType);
    if (columnPermIds.length === 0) return false;
    const selectedCount = columnPermIds.filter((id) => internalSelected.includes(id)).length;
    return selectedCount > 0 && selectedCount < columnPermIds.length;
  };

  // Helper function to render checkbox column with select all header
  const renderCheckboxColumn = (actionType: string, width = 100) => {
    // Convert snake_case to camelCase for translation keys
    // view_all -> viewAll, view_details -> viewDetails
    const camelCaseKey = actionType.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    return {
      title: (
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '4px' }}>
            {t(`permissions.columns.${camelCaseKey}`, actionType)}
          </div>
          <Checkbox
            checked={isColumnAllSelected(actionType)}
            indeterminate={isColumnIndeterminate(actionType)}
            onChange={(e) => handleSelectAllColumn(actionType, e.target.checked)}
          />
        </div>
      ),
      key: actionType,
      width,
      align: 'center' as const,
      render: (_: any, record: any) => {
        const perm = record.permissions[actionType];
        if (!perm) return null;
        return (
          <Checkbox
            checked={internalSelected.includes(perm.id)}
            onChange={(e) => handlePermissionChange(perm.id, e.target.checked)}
          />
        );
      },
    };
  };

  // Define table columns dynamically - need to handle multiple models with different column sets
  // For now, we'll use a unified column set that shows/hides based on model
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: t('permissions.columns.model', 'Model'),
        dataIndex: 'model',
        key: 'model',
        width: 200,
        fixed: 'left' as const,
        render: (model: any) => (
          <NameByLang
            name_en={model.name_en}
            name_ar={model.name_ar}
            name={model.name || model.key}
          />
        ),
      },
    ];

    return [
      ...baseColumns,
      renderCheckboxColumn('view_all', 100),
      renderCheckboxColumn('view_details', 120),
      renderCheckboxColumn('create', 100),
      renderCheckboxColumn('update', 100),
      renderCheckboxColumn('delete', 100),
    ];
  }, [tableData, t, internalSelected]);

  // Add "Other" column to all column sets
  const finalColumns = useMemo(() => {
    return [
      ...columns,
      {
        title: (
          <div style={{ textAlign: 'right' }}>
            <div style={{ marginBottom: '4px' }}>{t('permissions.columns.other', 'Other')}</div>
            <Checkbox
              checked={isColumnAllSelected('other')}
              indeterminate={isColumnIndeterminate('other')}
              onChange={(e) => handleSelectAllColumn('other', e.target.checked)}
            />
          </div>
        ),
        key: 'other',
        width: 250,
        align: 'left' as const,
        render: (_: any, record: any) => {
          const otherPerms = record.permissions.other;
          if (!otherPerms || !Array.isArray(otherPerms) || otherPerms.length === 0) {
            return null;
          }
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {otherPerms.map((perm: any) => (
                <Checkbox
                  key={perm.id}
                  checked={internalSelected.includes(perm.id)}
                  onChange={(e) => handlePermissionChange(perm.id, e.target.checked)}
                >
                  <NameByLang
                    name_en={perm.display_name_en}
                    name_ar={perm.display_name_ar}
                    name={perm.display_name || perm.name}
                  />
                </Checkbox>
              ))}
            </div>
          );
        },
      },
    ];
  }, [columns, t, internalSelected]);

  return (
    <div>
      <Table
        columns={finalColumns}
        dataSource={tableData}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="small"
      />
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-secondary)' }}>
        {t('permissions.selectionSummary', '{{selected}} permissions selected', {
          selected: internalSelected.length,
        })}
      </div>
    </div>
  );
};

export default PermissionsTable;
